import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Robot, Lightning, Trophy, ArrowClockwise } from '@phosphor-icons/react';

export default function AutoSignalEngine() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/admin/auto-signal/status'); setStatus(data); } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/auto-signal/run-now', {});
      toast.success(`Scan complete: ${data.published || 0} signal(s) published`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setRunning(false); }
  };

  if (!status) return <div className="glass p-6 rounded-lg text-muted-foreground">Loading engine status…</div>;

  return (
    <div className="space-y-4" data-testid="engine-panel">
      <div className="glass p-6 rounded-lg neon-border">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron">
              <Robot size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">Auto Signal Engine</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Running · scans every {status.interval_minutes} min · {status.instruments.length} instruments
              </div>
            </div>
          </div>
          <Button onClick={runNow} disabled={running} data-testid="engine-run-now" className="bg-saffron hover:bg-saffron-hover text-white">
            <ArrowClockwise size={16} className={`mr-2 ${running ? 'animate-spin' : ''}`} weight="bold" />
            {running ? 'Scanning…' : 'Run scan now'}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          {[
            {l:'Open Auto', v: status.open_auto_signals, c: 'text-blue-400'},
            {l:'Total Auto', v: status.auto_total, c: 'text-white'},
            {l:'Wins', v: status.auto_wins, c: 'text-green-400'},
            {l:'Losses', v: status.auto_losses, c: 'text-red-400'},
            {l:'Win Rate', v: status.auto_win_rate + '%', c: 'text-saffron'},
          ].map(({l,v,c}) => (
            <div key={l} className="p-3 rounded-md bg-surface-base border border-white/5">
              <div className="data-label">{l}</div>
              <div className={`stat-num text-2xl mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass p-6 rounded-lg">
        <h3 className="font-heading font-bold text-lg mb-3">Scanned Instruments</h3>
        <div className="flex flex-wrap gap-2">
          {status.instruments.map(i => (
            <span key={i} className="text-xs px-2 py-1 rounded bg-surface-base border border-white/5 font-mono">{i}</span>
          ))}
        </div>
      </div>

      <div className="glass p-6 rounded-lg">
        <h3 className="font-heading font-bold text-lg mb-3">Recent Scans (last 20)</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {status.recent_scans.length === 0 && <div className="text-sm text-muted-foreground">No scans yet.</div>}
          {status.recent_scans.map(s => (
            <div key={s.id} className="p-3 rounded-md bg-surface-base border border-white/5 text-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-mono text-xs text-muted-foreground">{new Date(s.started_at).toLocaleString()}</div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-widest font-bold ${s.status === 'ok' ? 'bg-green-500/15 text-green-400' : s.status === 'running' ? 'bg-blue-500/15 text-blue-400' : 'bg-red-500/15 text-red-400'}`}>{s.status}</span>
                  {s.published_count > 0 ? (
                    <span className="text-xs text-saffron font-semibold">+{s.published_count} signal{s.published_count>1?'s':''}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">No setups</span>
                  )}
                </div>
              </div>
              {s.published && s.published.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.published.map(p => (
                    <span key={p.id} className="text-[10px] px-2 py-0.5 rounded bg-saffron/10 text-saffron">
                      {p.instrument} {p.direction} · {p.confidence}% · {p.telegram ? '📢' : ''}
                    </span>
                  ))}
                </div>
              )}
              {s.note && !s.published_count && <div className="text-xs text-muted-foreground mt-1 italic">{s.note}</div>}
              {s.error && <div className="text-xs text-red-400 mt-1">{s.error}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
